import React, { useRef } from 'react';
import { Avatar, Spinner } from '@heroui/react';
import { useUsers } from '~/hooks/useUsers';
import { useTranslation } from 'react-i18next';

interface MentionSuggestionProps {
  query: string;
  onSelect: (user: { id: string; username: string }) => void;
  onClose: () => void;
  position?: { top: number; left: number };
  placement?: 'top' | 'bottom';
}

export const MentionSuggestion: React.FC<MentionSuggestionProps> = ({
  query,
  onSelect,
}) => {
  const { t } = useTranslation();
  const containerRef = useRef<HTMLDivElement>(null);
  
  // Fetch users based on the query
  const { users, isLoading } = useUsers({
    searchQuery: query,
    page: 1,
    pageSize: 5,
  });
  
  return (
    <div
      ref={containerRef}
      className={`bg-background border border-secondary-200 shadow-lg rounded-md w-full overflow-hidden`}
    >
      {isLoading ? (
        <div className="flex justify-center items-center p-4">
          <Spinner size="sm" color="warning" />
        </div>
      ) : users.length > 0 ? (
        <ul className="py-1 overflow-auto max-h-[250px] scrollbar-thin scrollbar-thumb-secondary-200 scrollbar-track-transparent">
          {users.map((user) => (
            <li 
              key={user.id}
              className="px-3 py-2 flex items-center gap-2 cursor-pointer hover:bg-secondary-100 transition-colors duration-150"
              onClick={() => onSelect(user)}
            >
              <Avatar 
                src={user.profilePictureUrl} 
                name={user.username}
                size="sm"
                radius="full"
                className="border border-secondary-200"
              />
              <div className="flex flex-col min-w-0">
                <span className="text-sm font-medium text-foreground-900 truncate">
                  {user.username}
                </span>
                {(user.firstName || user.lastName) && (
                  <span className="text-xs text-secondary-500 truncate">
                    {[user.firstName, user.lastName].filter(Boolean).join(' ')}
                  </span>
                )}
              </div>
            </li>
          ))}
        </ul>
      ) : query ? (
        <div className="p-3 text-sm text-secondary-500 text-center">
          {t('mentions.noUsersFound')}
        </div>
      ) : (
        <div className="p-3 text-sm text-secondary-500 text-center">
          {t('mentions.typeToSearch')}
        </div>
      )}
    </div>
  );
};

export default MentionSuggestion; 